import React from "react";

interface ResizeHandleProps {
	/** Forwarded to the hook's `startResize`. */
	onPointerDown: (event: React.PointerEvent) => void;
	/** Whether a drag is currently in progress (drives the active highlight). */
	isResizing: boolean;
	/** Which edge of the parent panel the handle sits on. */
	edge: "left" | "right";
	/** Accessible label for the separator. */
	label?: string;
	/** Reset to the default width. Also makes the handle keyboard-operable. */
	onReset?: () => void;
	/** Signed pixel delta from an arrow key, when the panel supports it. */
	onNudge?: (delta: number) => void;
	/** Current width, for the separator's value semantics. */
	value?: number;
	min?: number;
	max?: number;
}

/** One arrow press. Shift multiplies it, for crossing a panel quickly. */
const KEYBOARD_STEP = 16;

/**
 * A thin, absolutely-positioned drag strip for resizing a side panel. The
 * parent must be `position: relative`. Renders a 6px hit area with a hairline
 * that brightens to teal on hover/active.
 */
const ResizeHandle: React.FC<ResizeHandleProps> = ({
	onPointerDown,
	isResizing,
	edge,
	label = "Resize panel",
	onReset,
	onNudge,
	value,
	min,
	max,
}) => {
	const sideClass = edge === "right" ? "right-0" : "left-0";
	const lineColor = isResizing ? "bg-[#008782]" : "bg-transparent";
	// Keyboard support only where the panel supplied handlers, so the existing
	// chat panels keep exactly the DOM (and tab order) they had.
	const interactive = Boolean(onNudge || onReset);

	const handleKeyDown = (event: React.KeyboardEvent) => {
		const step = event.shiftKey ? KEYBOARD_STEP * 4 : KEYBOARD_STEP;
		// A handle on the LEFT edge grows its panel when dragged left, so the
		// arrow that grows it is the mirror of one on the right edge.
		const grow = edge === "left" ? -1 : 1;
		if (event.key === "ArrowLeft" && onNudge) {
			event.preventDefault();
			onNudge(-step * grow);
		} else if (event.key === "ArrowRight" && onNudge) {
			event.preventDefault();
			onNudge(step * grow);
		} else if ((event.key === "Home" || event.key === "Enter") && onReset) {
			event.preventDefault();
			onReset();
		}
	};

	return (
		<div
			role="separator"
			aria-orientation="vertical"
			aria-label={label}
			aria-valuenow={value}
			aria-valuemin={min}
			aria-valuemax={max}
			tabIndex={interactive ? 0 : undefined}
			onKeyDown={interactive ? handleKeyDown : undefined}
			onDoubleClick={onReset}
			onPointerDown={onPointerDown}
			className={`group absolute top-0 ${sideClass} z-20 h-full w-1.5 cursor-col-resize touch-none select-none focus:outline-none focus-visible:bg-[#008782]/40`}
		>
			<div
				className={`mx-auto h-full w-px transition-colors group-hover:bg-[#008782] ${lineColor}`}
			/>
		</div>
	);
};

export default ResizeHandle;
